import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, FileText, Download, Pencil, Check, X, Clock, FileDown, Eye, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { eur, lineTotal, quoteFileName, pct, fmtQty } from "@/lib/format";
import { QuoteSheet } from "@/components/QuoteSheet";
import { QUOTE_COLORS, colorBg } from "@/lib/colors";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const STATUS = {
  pending: { label: "Pendiente", cls: "bg-orange-100 text-orange-700", icon: Clock },
  accepted: { label: "Aceptado", cls: "bg-green-100 text-green-700", icon: Check },
  rejected: { label: "Rechazado", cls: "bg-red-100 text-red-700", icon: X },
};
const NO_CLIENT = "none";
const uid = () => (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));
const emptyItem = () => ({ _uid: uid(), description: "", quantity: 1, price: 0, discount: 0, note_above: "", note_below: "" });

const todayInput = () => new Date().toISOString().slice(0, 10);
const toDisplayDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};
const toInputDate = (display) => {
  if (!display) return todayInput();
  const parts = display.split("/");
  if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
  return display; // already ISO
};

export default function Quotes() {
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [settings, setSettings] = useState({});
  const [logoUrl, setLogoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [clientId, setClientId] = useState(NO_CLIENT);
  const [items, setItems] = useState([emptyItem()]);
  const [quoteDate, setQuoteDate] = useState(todayInput());
  const [quoteNumber, setQuoteNumber] = useState("");
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);
  const [preview, setPreview] = useState(null); // { quote, client }
  const [view, setView] = useState("quote"); // quote | service
  const [clientSearch, setClientSearch] = useState("");
  const [clientPickerOpen, setClientPickerOpen] = useState(false);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [q, c, s] = await Promise.all([api.get("/quotes", { params: { kind: view } }), api.get("/clients"), api.get("/settings")]);
      setQuotes(q.data);
      setClients(c.data);
      setSettings(s.data);
      if (s.data.logo_path) {
        try {
          const l = await api.get("/settings/logo", { responseType: "blob" });
          setLogoUrl(URL.createObjectURL(l.data));
        } catch (e) { console.error("Logo load error:", e); }
      }
    } finally {
      setLoading(false);
    }
  }, [view]);

  useEffect(() => { load(); }, [load]);

  const total = items.reduce((s, it) => s + lineTotal(it), 0);

  const sortedClients = [...clients].sort((a, b) =>
    (a.is_service ? 1 : 0) - (b.is_service ? 1 : 0) || a.name.localeCompare(b.name));

  const sq = search.trim().toLowerCase();
  const visibleQuotes = sq
    ? quotes.filter((q) =>
        String(q.number || "").includes(sq) ||
        (q.client_name || "").toLowerCase().includes(sq) ||
        (q.date || "").toLowerCase().includes(sq) ||
        (q.items || []).some((it) => (it.description || "").toLowerCase().includes(sq)))
    : quotes;

  const openNew = () => {
    setEditing(null); setClientId(NO_CLIENT); setItems([emptyItem()]);
    setQuoteDate(todayInput()); setQuoteNumber(""); setClientSearch(""); setOpen(true);
  };
  const openEdit = (q) => {
    setEditing(q.id); setClientId(q.client_id || NO_CLIENT);
    setItems(q.items.length ? q.items.map((i) => ({ ...i, _uid: uid() })) : [emptyItem()]);
    setQuoteDate(toInputDate(q.date)); setQuoteNumber(String(q.number || "")); setClientSearch(""); setOpen(true);
  };

  const updateItem = (idx, field, value) => setItems(items.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  const addItem = () => setItems([...items, emptyItem()]);
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const save = async () => {
    const cleaned = items
      .filter((it) => it.description.trim() || it.price || it.quantity)
      .map((it) => ({
        description: it.description,
        quantity: Number(it.quantity) || 0,
        price: Number(it.price) || 0,
        discount: Number(it.discount) || 0,
        note_above: it.note_above || "",
        note_below: it.note_below || "",
      }));
    if (cleaned.length === 0) { toast.error("Añade al menos un concepto"); return; }
    setSaving(true);
    try {
      const payload = {
        client_id: clientId === NO_CLIENT ? null : clientId,
        items: cleaned,
        date: toDisplayDate(quoteDate),
        kind: view,
      };
      if (view === "quote") {
        const num = parseInt(quoteNumber, 10);
        if (!isNaN(num) && num > 0) payload.number = num;
      }
      if (editing) await api.put(`/quotes/${editing}`, payload);
      else await api.post("/quotes", payload);
      toast.success(editing ? "Guardado" : "Creado");
      setOpen(false);
      load();
    } catch (e) {
      toast.error("No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (q, status) => {
    setBusy(q.id);
    try {
      const res = await api.post(`/quotes/${q.id}/status`, null, { params: { status } });
      if (res.data.created_job) toast.success("Aceptado. Se creó un trabajo pendiente para el cliente");
      else toast.success("Estado actualizado");
      load();
    } catch (e) {
      toast.error("No se pudo actualizar");
    } finally {
      setBusy(null);
    }
  };

  const remove = async (q) => {
    setBusy(q.id);
    try {
      await api.delete(`/quotes/${q.id}`);
      toast.success("Presupuesto eliminado");
      load();
    } catch (e) {
      toast.error("No se pudo eliminar");
    } finally {
      setBusy(null);
    }
  };

  const downloadPdf = async (q) => {
    setBusy(q.id);
    try {
      const res = await api.get(`/quotes/${q.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = quoteFileName(q);
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("No se pudo generar el PDF");
    } finally {
      setBusy(null);
    }
  };

  const openPreview = async (q) => {
    setBusy(q.id);
    try {
      const res = await api.get(`/quotes/${q.id}`);
      setPreview({ quote: { ...q, ...res.data }, client: res.data.client });
    } catch (e) {
      toast.error("No se pudo cargar la vista previa");
    } finally {
      setBusy(null);
    }
  };

  const setColor = async (q, color) => {
    setQuotes((prev) => prev.map((x) => x.id === q.id ? { ...x, color } : x));
    try {
      await api.post(`/quotes/${q.id}/color`, null, { params: color ? { color } : {} });
    } catch (e) {
      toast.error("No se pudo cambiar el color");
      load();
    }
  };

  const shareWhatsApp = (q) => {
    const phone = (q.client_phone || "").replace(/\D/g, "");
    const link = `${window.location.origin}/p/quote/${q.id}`;
    const msg = encodeURIComponent(`Buenas, adjunto presupuesto del servicio. Gracias\n${link}`);
    const url = phone ? `https://wa.me/${phone}?text=${msg}` : `https://wa.me/?text=${msg}`;
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="space-y-6" data-testid="quotes-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">{view === "service" ? "Servicios" : "Presupuestos"}</h1>
          <p className="text-muted-foreground mt-1">{quotes.length} en total</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-md border border-border overflow-hidden">
            <button onClick={() => setView("quote")} data-testid="view-quotes"
              className={`px-3 py-2 text-sm font-medium ${view === "quote" ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground"}`}>
              Presupuestos
            </button>
            <button onClick={() => setView("service")} data-testid="view-services"
              className={`px-3 py-2 text-sm font-medium ${view === "service" ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground"}`}>
              Servicios
            </button>
          </div>
          <Button onClick={openNew} data-testid="new-quote-button">
            <Plus className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">{view === "service" ? "Nuevo servicio" : "Nuevo presupuesto"}</span>
          </Button>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Buscar por número, cliente o concepto…" value={search}
          onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="quote-search" />
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : quotes.length === 0 ? (
        <div className="rounded-md border border-dashed border-border p-10 text-center text-muted-foreground">
          <FileText className="h-8 w-8 mx-auto mb-3 opacity-40" />
          {view === "service" ? "No hay servicios todavía." : "No hay presupuestos todavía."}
        </div>
      ) : visibleQuotes.length === 0 ? (
        <div className="rounded-md border border-dashed border-border p-10 text-center text-muted-foreground">
          No hay resultados para “{search}”.
        </div>
      ) : (
        <div className="space-y-3" data-testid="quotes-list">
          {visibleQuotes.map((q) => {
            const st = STATUS[q.status] || STATUS.pending;
            const StIcon = st.icon;
            return (
              <div key={q.id} data-testid={`quote-item-${q.id}`}
                style={{ backgroundColor: colorBg(QUOTE_COLORS, q.color) }}
                className="rounded-md border border-border p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-display font-semibold">{q.number != null ? `N.º ${q.number}` : "Servicio"}</span>
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${st.cls}`}
                        data-testid={`quote-status-${q.id}`}>
                        <StIcon className="h-3 w-3" /> {st.label}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5 truncate">{q.client_name} · {q.date}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-display font-bold text-lg">{eur(q.total)}</p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 mt-4 pt-3 border-t border-border">
                  <Button size="sm" variant="outline" onClick={() => openPreview(q)} disabled={busy === q.id}
                    data-testid={`quote-preview-${q.id}`}>
                    {busy === q.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                    <span className="ml-1.5">Vista previa</span>
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => downloadPdf(q)} disabled={busy === q.id}
                    data-testid={`quote-pdf-${q.id}`}>
                    <FileDown className="h-4 w-4" />
                    <span className="ml-1.5">PDF</span>
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => shareWhatsApp(q)}
                    data-testid={`quote-whatsapp-${q.id}`} className="text-green-700 border-green-200 hover:bg-green-50">
                    WhatsApp
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => openEdit(q)} data-testid={`quote-edit-${q.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <div className="flex items-center gap-1" data-testid={`quote-colors-${q.id}`}>
                    {QUOTE_COLORS.map((c) => (
                      <button key={c.key || "none"} type="button" title={c.label}
                        onClick={() => setColor(q, c.key)}
                        data-testid={`quote-color-${q.id}-${c.key || "none"}`}
                        className={`h-5 w-5 rounded-full border-2 transition-transform ${q.color === c.key || (!q.color && !c.key) ? "border-primary scale-110" : "border-border"}`}
                        style={{ backgroundColor: c.bg }} />
                    ))}
                  </div>
                  <div className="flex-1" />
                  {q.status !== "accepted" && (
                    <Button size="sm" onClick={() => setStatus(q, "accepted")} disabled={busy === q.id}
                      data-testid={`quote-accept-${q.id}`} className="bg-green-600 hover:bg-green-700">
                      <Check className="h-4 w-4 mr-1" /> Aceptar
                    </Button>
                  )}
                  {q.status !== "rejected" && (
                    <Button size="sm" variant="outline" onClick={() => setStatus(q, "rejected")} disabled={busy === q.id}
                      data-testid={`quote-reject-${q.id}`} className="text-destructive border-destructive/30 hover:bg-destructive/5">
                      <X className="h-4 w-4 mr-1" /> Rechazar
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => remove(q)} data-testid={`quote-delete-${q.id}`}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Editor */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quote-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar presupuesto" : "Nuevo presupuesto"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Cliente (opcional)</Label>
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger data-testid="quote-client-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <div className="px-2 pb-2 sticky top-0 bg-popover">
                    <Input placeholder="Buscar cliente…" value={clientSearch}
                      onChange={(e) => setClientSearch(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      data-testid="quote-client-search" className="h-8" />
                  </div>
                  <SelectItem value={NO_CLIENT}>Sin cliente</SelectItem>
                  {sortedClients
                    .filter((c) =>
                      c.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
                      (c.phone || "").toLowerCase().includes(clientSearch.toLowerCase()))
                    .map((c) => <SelectItem key={c.id} value={c.id}>{c.is_service ? "🔧 " : ""}{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Fecha del presupuesto</Label>
                <Input type="date" value={quoteDate} onChange={(e) => setQuoteDate(e.target.value)}
                  data-testid="quote-date-input" />
              </div>
              <div>
                <Label>N.º de presupuesto (opcional)</Label>
                <Input type="number" inputMode="numeric" placeholder="Automático (correlativo)"
                  value={quoteNumber} onChange={(e) => setQuoteNumber(e.target.value)}
                  data-testid="quote-number-input" />
              </div>
            </div>

            <div>
              <Label>Conceptos</Label>
              {/* Column headers (desktop) */}
              <div className="hidden sm:grid grid-cols-12 gap-2 px-1 mt-1 mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <div className="col-span-5">Descripción</div>
                <div className="col-span-2">Cant.</div>
                <div className="col-span-2">Precio</div>
                <div className="col-span-2">Dto. %</div>
                <div className="col-span-1"></div>
              </div>
              <div className="space-y-3 sm:space-y-2 mt-1">
                {items.map((it, idx) => (
                  <div key={it._uid || idx} className="grid grid-cols-12 gap-2 items-end sm:items-center rounded-md sm:rounded-none border sm:border-0 border-border p-2 sm:p-0"
                    data-testid={`quote-item-row-${idx}`}>
                    <div className="col-span-12 sm:col-span-5">
                      <span className="sm:hidden text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Descripción</span>
                      <Input placeholder="Descripción" value={it.description}
                        onChange={(e) => updateItem(idx, "description", e.target.value)} data-testid={`item-desc-${idx}`} />
                    </div>
                    <div className="col-span-4 sm:col-span-2">
                      <span className="sm:hidden text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Cant.</span>
                      <Input type="number" inputMode="decimal" placeholder="Cant." value={it.quantity}
                        onChange={(e) => updateItem(idx, "quantity", e.target.value)} data-testid={`item-qty-${idx}`} />
                    </div>
                    <div className="col-span-4 sm:col-span-2">
                      <span className="sm:hidden text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Precio</span>
                      <Input type="number" inputMode="decimal" placeholder="Precio" value={it.price}
                        onChange={(e) => updateItem(idx, "price", e.target.value)} data-testid={`item-price-${idx}`} />
                    </div>
                    <div className="col-span-3 sm:col-span-2">
                      <span className="sm:hidden text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Dto. %</span>
                      <Input type="number" inputMode="decimal" placeholder="Dto. %" value={it.discount}
                        onChange={(e) => updateItem(idx, "discount", e.target.value)} data-testid={`item-discount-${idx}`} />
                    </div>
                    <div className="col-span-1 flex justify-center pb-2 sm:pb-0">
                      <button onClick={() => removeItem(idx)} className="text-muted-foreground hover:text-destructive"
                        data-testid={`item-remove-${idx}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="col-span-12 sm:hidden text-right text-xs text-muted-foreground">
                      Línea: {eur(lineTotal(it))}
                    </div>
                    <div className="col-span-12 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <Input placeholder="Comentario encima (opcional)" value={it.note_above || ""}
                        onChange={(e) => updateItem(idx, "note_above", e.target.value)}
                        data-testid={`item-note-above-${idx}`} className="text-xs" />
                      <Input placeholder="Comentario debajo (opcional)" value={it.note_below || ""}
                        onChange={(e) => updateItem(idx, "note_below", e.target.value)}
                        data-testid={`item-note-below-${idx}`} className="text-xs" />
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={addItem} className="mt-2" data-testid="add-item-button">
                <Plus className="h-4 w-4 mr-1" /> Añadir concepto
              </Button>
            </div>

            <div className="flex items-center justify-between rounded-md bg-accent p-4">
              <span className="font-semibold">Total</span>
              <span className="font-display font-bold text-xl" data-testid="quote-total">{eur(total)}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="save-quote-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />} Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* HTML preview */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl w-[95vw] max-h-[92vh] flex flex-col p-0 gap-0" data-testid="quote-preview-dialog">
          <DialogHeader className="p-4 border-b border-border">
            <DialogTitle>Vista previa · Presupuesto N.º {preview?.quote?.number}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto bg-slate-100 p-4 sm:p-6">
            {preview && (
              <QuoteSheet quote={preview.quote} client={preview.client} settings={settings} logoUrl={logoUrl} />
            )}
          </div>
          <DialogFooter className="p-4 border-t border-border">
            <Button variant="outline" onClick={() => preview && shareWhatsApp(preview.quote)}
              data-testid="preview-whatsapp" className="text-green-700 border-green-200 hover:bg-green-50">
              WhatsApp
            </Button>
            <Button onClick={() => preview && downloadPdf(preview.quote)} data-testid="preview-download">
              <Download className="h-4 w-4 mr-1.5" /> Descargar PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
